import type { PlaceFromWebsiteResponse, WebsitePhoto, WeekHours } from '@pulsify/shared/dto'
import { normalizeCountry } from '~/shared/lib/countries'
import {
  type CreateListingInput,
  type OpeningHour,
  type PriceLevelValue,
  toPriceLevelValue,
} from '~/shared/types'

export type WeekdayKey = keyof WeekHours
export type HoursRange = WeekHours[WeekdayKey][number]

export const WEEK_ORDER: WeekdayKey[] = ['1', '2', '3', '4', '5', '6', '0']

const DAY_NAME: Record<WeekdayKey, OpeningHour['day']> = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
}

function emptyHours(): WeekHours {
  return { '0': [], '1': [], '2': [], '3': [], '4': [], '5': [], '6': [] }
}

export interface PlaceDraft {
  name: string
  description: string
  phone: string
  email: string
  website: string
  instagram: string
  facebook: string
  twitter: string
  street: string
  postalCode: string
  city: string
  country: string
  priceLevel: PriceLevelValue
  hours: WeekHours
  categoryId: string | undefined
  subCategoryIds: string[]
}

/** A photo found on the website, or one the person added or adjusted themselves. */
export type PickedPhoto = Pick<WebsitePhoto, 'sourceUrl' | 'dataBase64'> & { mimeType: string }

export interface PhotoPick {
  photo: PickedPhoto
  selected: boolean
}

export function draftFromWebsite(place: PlaceFromWebsiteResponse): PlaceDraft {
  return {
    name: place.name,
    description: place.description,
    phone: place.phone ?? '',
    email: place.email ?? '',
    website: place.website,
    instagram: place.instagram ?? '',
    facebook: place.facebook ?? '',
    twitter: place.twitter ?? '',
    street: place.location.street,
    postalCode: place.location.postalCode,
    city: place.location.city,
    country: normalizeCountry(place.location.country),
    priceLevel: toPriceLevelValue(place.priceLevel),
    hours: { ...emptyHours(), ...place.hours },
    categoryId: place.tags.mainCategoryId || undefined,
    subCategoryIds: place.tags.subCategoryTags,
  }
}

function toOpeningHours(hours: WeekHours): OpeningHour[] {
  const result: OpeningHour[] = []
  for (const key of WEEK_ORDER) {
    for (const range of hours[key] ?? []) {
      if (!range.open || !range.close) continue
      result.push({
        day: DAY_NAME[key],
        open: `${range.open.slice(0, 5)}:00`,
        close: `${range.close.slice(0, 5)}:00`,
      })
    }
  }
  return result
}

export function toCreateListingInput(draft: PlaceDraft): CreateListingInput {
  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    location: {
      name: draft.name.trim(),
      street: draft.street.trim(),
      city: draft.city.trim(),
      country: draft.country.trim(),
    },
    postalCode: draft.postalCode.trim(),
    priceLevel: draft.priceLevel,
    phone: draft.phone.trim(),
    email: draft.email.trim(),
    website: draft.website.trim(),
    facebook: draft.facebook.trim(),
    instagram: draft.instagram.trim(),
    twitter: draft.twitter.trim(),
    nicknames: '',
    hours: toOpeningHours(draft.hours),
    isDraft: false,
    tags: {
      mainCategoryId: draft.categoryId ?? '',
      subCategoryTags: draft.subCategoryIds,
      subSubCategoryTags: [],
      amenityIds: [],
    },
  }
}

export type DraftProblem = 'name' | 'address' | 'category' | 'description'

export function draftProblem(draft: PlaceDraft): DraftProblem | null {
  if (!draft.name.trim()) return 'name'
  if (!draft.street.trim() || !draft.city.trim() || !draft.country.trim()) return 'address'
  if (!draft.categoryId || draft.subCategoryIds.length === 0) return 'category'
  if (draft.description.trim().length < 10) return 'description'
  return null
}

function rangeLabel(ranges: HoursRange[]): string {
  return ranges.map((range) => `${range.open}–${range.close}`).join(', ')
}

export function summarizeHours(hours: WeekHours, dayLabels: Record<WeekdayKey, string>): string {
  const groups: { from: WeekdayKey; to: WeekdayKey; label: string }[] = []
  for (const key of WEEK_ORDER) {
    const ranges = hours[key] ?? []
    if (ranges.length === 0) continue
    const label = rangeLabel(ranges)
    const last = groups[groups.length - 1]
    const previousKey = WEEK_ORDER[WEEK_ORDER.indexOf(key) - 1]
    if (last && last.label === label && last.to === previousKey) {
      last.to = key
    } else {
      groups.push({ from: key, to: key, label })
    }
  }
  return groups
    .map((group) =>
      group.from === group.to
        ? `${dayLabels[group.from]} ${group.label}`
        : `${dayLabels[group.from]}–${dayLabels[group.to]} ${group.label}`,
    )
    .join(' · ')
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export function normalizeUrl(input: string): string | null {
  const trimmed = input.trim()
  if (!trimmed || /\s/.test(trimmed)) return null
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(candidate)
    if (!url.hostname.includes('.')) return null
    return url.toString()
  } catch {
    return null
  }
}

export function photoSrc(photo: PickedPhoto): string {
  return `data:${photo.mimeType};base64,${photo.dataBase64}`
}

export function photoFile(photo: PickedPhoto, index: number): File {
  const binary = atob(photo.dataBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const extension = photo.mimeType.split('/')[1]
  return new File([bytes], `website-photo-${index + 1}.${extension}`, { type: photo.mimeType })
}

export function orderedPhotos(photos: PhotoPick[], coverUrl: string | null): PickedPhoto[] {
  const selected = photos.filter((pick) => pick.selected).map((pick) => pick.photo)
  return [
    ...selected.filter((photo) => photo.sourceUrl === coverUrl),
    ...selected.filter((photo) => photo.sourceUrl !== coverUrl),
  ]
}

export function photoFromFile(file: File, sourceUrl: string): Promise<PickedPhoto> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () =>
      resolve({
        sourceUrl,
        mimeType: file.type,
        dataBase64: String(reader.result).split(',')[1] ?? '',
      })
    reader.readAsDataURL(file)
  })
}
